import React, { useState, useEffect } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import Tooltip from '../components/Tooltip';

type NotifPref = { event_type: string; channel: string; email_override: string | null };

const COACH_EVENTS: { key: string; label: string; desc: string }[] = [
  { key: 'session_reminder',     label: 'Session Reminders',   desc: 'Reminders before each upcoming coaching session' },
  { key: 'form_submitted',       label: 'Form submitted',       desc: 'A client submits a form or questionnaire you assigned' },
  { key: 'agreement_signed',     label: 'Agreement signed',     desc: 'A client signs a document packet' },
  { key: 'subscription_invoice', label: 'CoachSight invoice',   desc: 'Your monthly CoachSight subscription invoice is generated' },
  { key: 'payment_received',     label: 'Payment received',     desc: 'A client completes a payment for a session or package' },
];

const CLIENT_EVENTS: { key: string; label: string; desc: string }[] = [
  { key: 'session_reminder',      label: 'Session Reminders',     desc: 'Reminders before each upcoming coaching session' },
  { key: 'session_notes_shared',  label: 'Notes / Action Items',  desc: 'Your coach shares session notes or action items with you' },
  { key: 'nudge',                 label: 'Nudges',                desc: 'Coaching reminders your coach sends to keep you on track' },
  { key: 'form_assigned',         label: 'Form assigned',         desc: 'Your coach assigns a form or questionnaire' },
  { key: 'form_due_reminder',     label: 'Form due reminder',     desc: 'A reminder that a form is due soon' },
  { key: 'invoice_sent',          label: 'Invoice sent',          desc: 'Your coach sends you an invoice' },
  { key: 'payment_confirmation',  label: 'Payment confirmation',  desc: 'A payment you made has been processed successfully' },
  { key: 'payment_failure',       label: 'Payment failure',       desc: 'A payment attempt was unsuccessful' },
];

const CHANNEL_LABELS: Record<string, string> = { email: 'Email', sms: 'SMS', both: 'Email & SMS', none: 'None' };

export default function NotificationPreferencesPage() {
  const token    = localStorage.getItem('authToken') || '';
  const userType = localStorage.getItem('userType') || '';

  const isCoach  = userType === 'coach';
  const isClient = userType === 'client';
  const loggedIn = !!(token && (isCoach || isClient));

  const EVENTS   = isCoach ? COACH_EVENTS : CLIENT_EVENTS;
  const prefsUrl = isCoach
    ? `${config.apiBaseUrl}/coaches/me/notification-preferences`
    : `${config.apiBaseUrl}/clients/me/notification-preferences`;
  const profileUrl = isCoach
    ? `${config.apiBaseUrl}/coaches/me`
    : `${config.apiBaseUrl}/clients/me`;

  const [prefs,       setPrefs]       = useState<NotifPref[]>([]);
  const [loading,     setLoading]     = useState(loggedIn);
  const [saving,      setSaving]      = useState(false);
  const [saveError,   setSaveError]   = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [phone,       setPhone]       = useState<string | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);

  useEffect(() => {
    if (!loggedIn) return;
    setLoading(true);
    Promise.all([
      fetch(prefsUrl,  { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch(profileUrl,{ headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ]).then(([prefData, profile]) => {
      setPrefs(Array.isArray(prefData) ? prefData : []);
      if (profile) {
        setPhone(profile.phone || null);
        setPhoneVerified(!!profile.is_phone_verified);
      }
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPref = (key: string): NotifPref =>
    prefs.find(p => p.event_type === key) ?? { event_type: key, channel: 'email', email_override: null };

  const updateChannel = (key: string, ch: string) => {
    setPrefs(prev => {
      const exists = prev.find(p => p.event_type === key);
      if (exists) return prev.map(p => p.event_type === key ? { ...p, channel: ch } : p);
      return [...prev, { event_type: key, channel: ch, email_override: null }];
    });
  };

  const smsIneligible = !phoneVerified || !phone || !phone.startsWith('+1');
  const hasSmsSelected = prefs.some(p => p.channel === 'sms' || p.channel === 'both');

  const save = async (overrideToEmail = false) => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const payload = EVENTS.map(ev => {
        const p = getPref(ev.key);
        return { event_type: ev.key, channel: overrideToEmail ? 'email' : p.channel, email_override: p.email_override || null };
      });
      const res = await fetch(prefsUrl, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(err.detail || 'Save failed. Please try again.');
        return;
      }
      const updated = await res.json();
      setPrefs(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3500);
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = () => save(true);

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gray-900 border border-gray-800 rounded-xl p-8 text-center space-y-4">
          <h1 className="text-xl font-bold text-white">Notification Preferences</h1>
          <p className="text-sm text-gray-400">Please log in to manage your notification preferences.</p>
          <a
            href="/login"
            className="inline-block px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Log in
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-sm text-gray-400">Loading preferences...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 py-12 px-4">
      <div className="max-w-2xl mx-auto">

        <div className="mb-8">
          <div className="mb-3">
            <span className="text-violet-400 font-semibold text-sm tracking-wide uppercase">CoachSight</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Notification Preferences</h1>
          <p className="text-gray-400 mt-1 text-sm">
            Choose how you'd like to receive each type of notification.
            Text message (SMS) notifications are available for verified US and Canada phone numbers.
          </p>
        </div>

        {/* SMS disclosure */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3 text-sm text-gray-400 mb-4">
          <p><span className="font-medium text-gray-300">Consent Statement:</span> By entering your phone number on your profile and choosing SMS as a notification delivery preference, you consent to receive SMS messages from CoachSight about coaching updates and notifications.</p>
          <p><span className="font-medium text-gray-300">Message Frequency:</span> Up to 50 messages per month depending on your activity and preferences. No marketing or promotional messages.</p>
          <p><span className="font-medium text-gray-300">Rates:</span> Message and data rates may apply.</p>
          <p><span className="font-medium text-gray-300">Opt-Out:</span> Reply STOP to unsubscribe at any time.</p>
          <p>
            <span className="font-medium text-gray-300">Terms &amp; Conditions and Privacy Policy:</span>{' '}
            See our <a href="https://www.coachsightecr.com/terms#6-sms-messaging-program" className="text-violet-400 hover:underline" target="_blank" rel="noreferrer">Terms &amp; Conditions</a> and{' '}
            <a href="https://www.coachsightecr.com/privacy#3-sms-messaging-program" className="text-violet-400 hover:underline" target="_blank" rel="noreferrer">Privacy Policy</a> for more information.
          </p>
        </div>

        {hasSmsSelected && smsIneligible && (
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 mb-4">
            <span className="text-amber-400 text-base leading-none mt-0.5">&#9888;</span>
            <p className="text-xs text-amber-300">
              SMS requires a verified US or Canada phone number. To verify your phone, go to Settings &rsaquo; Profile in your dashboard.
            </p>
          </div>
        )}

        {/* Event rows */}
        <div className="space-y-3 mb-6">
          {EVENTS.map(ev => {
            const p = getPref(ev.key);
            return (
              <div key={ev.key} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="text-sm font-medium text-white">{ev.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{ev.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['email', 'sms', 'both', 'none'] as const).map(ch => {
                    const isSmsOption = ch === 'sms' || ch === 'both';
                    const isDisabled = isSmsOption && smsIneligible;
                    const isActive = p.channel === ch && !isDisabled;
                    const btn = (
                    <button
                      key={ch}
                      onClick={() => !isDisabled && updateChannel(ev.key, ch)}
                      disabled={isDisabled}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors font-medium ${
                        isActive
                          ? ch === 'none'
                            ? 'bg-gray-700 border-gray-600 text-gray-300'
                            : 'bg-violet-600 border-violet-600 text-white'
                          : isDisabled
                            ? 'border-gray-800 text-gray-700 cursor-not-allowed bg-gray-900/50'
                            : 'border-gray-700 text-gray-400 hover:border-violet-500 hover:text-white'
                      }`}
                    >
                      {CHANNEL_LABELS[ch]}
                    </button>
                    );
                    return isDisabled
                      ? <Tooltip key={ch} text="Requires a verified US or Canada phone number">{btn}</Tooltip>
                      : <React.Fragment key={ch}>{btn}</React.Fragment>;
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {saveError && (
          <p className="text-sm text-red-400 mb-3">{saveError}</p>
        )}

        {/* Save */}
        <div className="flex items-center gap-4 mb-10">
          <button
            onClick={() => save()}
            disabled={saving}
            className="px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {saving ? 'Saving...' : 'Save preferences'}
          </button>
          {saveSuccess && (
            <span className="flex items-center gap-1.5 text-sm text-green-400">
              <CheckCircleIcon className="w-4 h-4" /> Saved.
            </span>
          )}
        </div>

        {/* SMS opt-out */}
        <div className="border-t border-gray-800 pt-8">
          <h2 className="text-sm font-semibold text-gray-300 mb-1">SMS opt-out</h2>
          <p className="text-xs text-gray-500 mb-4">
            You can stop all SMS notifications at any time by replying <strong className="text-gray-400">STOP</strong> to
            any text message from us, or by clicking the button below.
            Standard message and data rates may apply.
            Reply <strong className="text-gray-400">HELP</strong> to any message for assistance.
          </p>
          <button
            onClick={handleUnsubscribeAll}
            disabled={saving}
            className="px-4 py-2 border border-gray-700 text-gray-400 hover:border-red-500 hover:text-red-400 disabled:opacity-50 text-sm rounded-lg transition-colors"
          >
            Unsubscribe from SMS
          </button>
        </div>

        <p className="text-xs text-gray-700 mt-10 text-center">
          CoachSight &mdash; <a href="/privacy" className="underline hover:text-gray-500">Privacy Policy</a> &middot; <a href="/terms" className="underline hover:text-gray-500">Terms of Use</a>
        </p>
      </div>
    </div>
  );
}
